if (!customElements.get('lookbook-icon')) {
  customElements.define(
    'lookbook-icon',
    class LookbookIcon extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          cardContainer: '.lookbook-card',
          cardProduct: '.lookbook-icon__card',
        };

        this.cardContainer = this.closest(this.selectors.cardContainer);
        this.cardProduct = this.querySelector(this.selectors.cardProduct);

        // Bind contexts to prevent memory leaks
        this.handleInteraction = this.handleInteraction.bind(this);
        this.handleResize = this.handleResize.bind(this);

        this.init();

        // Native theme breakpoint triggers
        document.addEventListener('matchMobile', this.handleInteraction);
        document.addEventListener('unmatchMobile', this.handleInteraction);

        // Clean, non-looping interaction triggers
        this.addEventListener('mouseenter', this.handleInteraction);
        this.addEventListener('click', this.handleInteraction);
        this.addEventListener('touchstart', this.handleInteraction, { passive: true });

        // Safely track window resize (debounced to save performance)
        this.resizeTimeout = null;
        window.addEventListener('resize', this.handleResize);
      }

      disconnectedCallback() {
        this.removeEventListener('mouseenter', this.handleInteraction);
        this.removeEventListener('click', this.handleInteraction);
        this.removeEventListener('touchstart', this.handleInteraction);
        window.removeEventListener('resize', this.handleResize);
      }

      handleInteraction() {
        this.init();
      }

      handleResize() {
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          // Only recalculate on resize if the hotspot is active or visible
          if (this.classList.contains('is-active') || (this.cardProduct && getComputedStyle(this.cardProduct).opacity !== '0')) {
            this.init();
          }
        }, 150);
      }

      // Transition-proof and hidden-proof measurement hack
      getCardDimensions() {
        if (!this.cardProduct) return { width: 294, height: 350 };

        // If card is already fully visible and rendering, grab its live size
        if (this.cardProduct.offsetWidth > 0 && this.cardProduct.offsetHeight > 0) {
          return {
            width: this.cardProduct.offsetWidth,
            height: this.cardProduct.offsetHeight
          };
        }

        // If card is hidden, temporarily mock its presentation off-screen to measure its natural blueprint
        const originalStyle = this.cardProduct.getAttribute('style') || '';
        
        this.cardProduct.style.setProperty('display', 'block', 'important');
        this.cardProduct.style.setProperty('visibility', 'hidden', 'important');
        this.cardProduct.style.setProperty('position', 'absolute', 'important');
        this.cardProduct.style.setProperty('transform', 'none', 'important');
        this.cardProduct.style.setProperty('max-height', 'none', 'important');
        this.cardProduct.style.setProperty('height', 'auto', 'important');
        this.cardProduct.style.setProperty('width', 'auto', 'important');

        const width = this.cardProduct.offsetWidth;
        const height = this.cardProduct.offsetHeight;

        this.cardProduct.setAttribute('style', originalStyle);

        return {
          width: width || 294,
          height: height || 350
        };
      }

      init() {
        if (!this.cardProduct || !this.cardContainer) return;

        // 1. Clear previous inline JS overrides to allow clean layout recalculation
        this.cardProduct.style.position = '';
        this.cardProduct.style.left = '';
        this.cardProduct.style.top = '';
        this.cardProduct.style.right = '';
        this.cardProduct.style.bottom = '';
        this.cardProduct.style.transform = '';
        this.cardProduct.style.maxHeight = '';
        this.cardProduct.style.overflowY = '';

        // 2. Capture coordinates and bounds
        const containerRect = this.cardContainer.getBoundingClientRect();
        const iconRect = this.getBoundingClientRect();

        this.cardContainerOffsetX = containerRect.left;
        this.cardContainerOffsetY = containerRect.top;

        this.offsetX = iconRect.left - this.cardContainerOffsetX;
        this.offsetY = iconRect.top - this.cardContainerOffsetY;

        this.width = this.clientWidth || iconRect.width;
        this.height = this.clientHeight || iconRect.height;

        // Get robust, transition-proof measurements
        const dimensions = this.getCardDimensions();
        this.cardWidth = dimensions.width;
        this.cardHeight = dimensions.height;

        this.wrapperWidth = this.cardContainer.clientWidth || containerRect.width;
        this.wrapperHeight = this.cardContainer.clientHeight || containerRect.height;

        // 3. Fallback position classes (Theme Logic)
        this.cardPosition = this.offsetY > this.cardHeight ? 'top' : 'bottom';

        if ((this.offsetX + this.width) > this.cardWidth) {
          this.cardPosition += '-left';
        } else if ((this.wrapperWidth - this.offsetX) > this.cardWidth) {
          this.cardPosition += '-right';
        } else {
          this.cardPosition += (this.offsetX > (this.wrapperWidth / 2)) ? '-left' : '-right';
        }

        this.dataset.position = this.cardPosition;

        // 4. Handle boundary adjustments
        const isMobile = window.innerWidth < 1024;
        const padding = 16; // Safe spacing from boundary edges (pixels)

        if (isMobile) {
          // --- MOBILE & TABLET MODE ---

          // A. Height protection: Cap height if popup exceeds container size
          const maxAvailableHeight = this.wrapperHeight - (padding * 2);
          if (this.cardHeight > maxAvailableHeight) {
            // MUST use setProperty with 'important' to break through default theme !important overrides
            this.cardProduct.style.setProperty('max-height', `${maxAvailableHeight}px`, 'important');
            this.cardProduct.style.setProperty('overflow-y', 'auto', 'important');
            this.cardHeight = maxAvailableHeight;
          }

          // B. Horizontal Clamp: Try centering over hotspot, clamp inside screen boundaries
          const idealLeft = (this.width / 2) - (this.cardWidth / 2);
          const minLeft = -this.offsetX + padding;
          const maxLeft = this.wrapperWidth - this.offsetX - this.cardWidth - padding;
          const finalLeft = Math.max(minLeft, Math.min(idealLeft, maxLeft));

          // C. Vertical Clamp: Intelligently pick direction, clamp if space is too tight
          const spaceAbove = this.offsetY;
          const spaceBelow = this.wrapperHeight - this.offsetY - this.height;
          let finalTop;

          if (spaceAbove > spaceBelow) {
            const idealTop = -this.cardHeight - 12; // 12px gap above
            finalTop = Math.max(-this.offsetY + padding, idealTop);
          } else {
            const idealTop = this.height + 12; // 12px gap below
            finalTop = Math.min(this.wrapperHeight - this.offsetY - this.cardHeight - padding, idealTop);
          }

          // Apply clean mobile inline coordinate overrides
          this.cardProduct.style.setProperty('position', 'absolute', 'important');
          this.cardProduct.style.setProperty('left', `${finalLeft}px`, 'important');
          this.cardProduct.style.setProperty('top', `${finalTop}px`, 'important');
          this.cardProduct.style.setProperty('right', 'auto', 'important');
          this.cardProduct.style.setProperty('bottom', 'auto', 'important');
          this.cardProduct.style.setProperty('transform', 'none', 'important');

        } else {
          // --- DESKTOP MODE ---
          // Run desktop measurement only when elements are fully rendered
          const cardRect = this.cardProduct.getBoundingClientRect();

          if (cardRect.width > 0 && cardRect.height > 0) {
            const overflowRight = cardRect.right - containerRect.right;
            const overflowLeft = containerRect.left - cardRect.left;
            const overflowBottom = cardRect.bottom - containerRect.bottom;
            const overflowTop = containerRect.top - cardRect.top;

            // Nudge horizontally if clipping
            if (overflowRight > 0) {
              this.cardProduct.style.setProperty('right', 'auto', 'important');
              const currentLeft = this.cardProduct.offsetLeft;
              this.cardProduct.style.setProperty('left', `${currentLeft - overflowRight - padding}px`, 'important');
            } else if (overflowLeft > 0) {
              this.cardProduct.style.setProperty('right', 'auto', 'important');
              const currentLeft = this.cardProduct.offsetLeft;
              this.cardProduct.style.setProperty('left', `${currentLeft + overflowLeft + padding}px`, 'important');
            }

            // Nudge vertically if clipping
            if (overflowBottom > 0) {
              this.cardProduct.style.setProperty('bottom', 'auto', 'important');
              const currentTop = this.cardProduct.offsetTop;
              this.cardProduct.style.setProperty('top', `${currentTop - overflowBottom - padding}px`, 'important');
            } else if (overflowTop > 0) {
              this.cardProduct.style.setProperty('bottom', 'auto', 'important');
              const currentTop = this.cardProduct.offsetTop;
              this.cardProduct.style.setProperty('top', `${currentTop + overflowTop + padding}px`, 'important');
            }
          }
        }

        // Shopify theme editor block triggers
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          this.addEventListener('shopify:block:select', () => {
            this.cardProduct.style.setProperty('transition', 'none', 'important');
            this.classList.add('is-active');
          });

          this.addEventListener('shopify:block:deselect', () => {
            this.classList.remove('is-active');
          });
        }
      }
    }
  );
}